import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { DEFAULT_PRICE_TABLE, getTableCols } from '../../config/constants';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn } from '../../config/styles';
import { convertPrice, buildProductGroups } from '../../utils/products';

// ─────────────────────────────────────────────────────────────
// CAD UPLOAD MODAL
// ─────────────────────────────────────────────────────────────

function CadUploadModal({ products, onAssign, onClose }) {
  const [files, setFiles]       = useState([]);       // [{ file, url, name }]
  const [matches, setMatches]   = useState([]);       // [{ file, url, name, matchedId, matchedTitle, confidence }]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');

  const normalise = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const fuzzyMatch = (filename, prods) => {
    const norm = normalise(filename.replace(/\.[^.]+$/, '')); // strip extension
    let best = null, bestScore = 0;
    for (const p of prods) {
      const sku   = normalise(p.skuBase);
      const title = normalise(p.titleNoColour || p.title);
      let score = 0;
      if (sku && norm.includes(sku))   score = 100;
      else if (title && norm.includes(title)) score = 80;
      else {
        // partial: count matching chars
        const words = (p.titleNoColour || p.title || '').toLowerCase().split(/\s+/).filter(w => w.length > 3);
        const hits  = words.filter(w => norm.includes(normalise(w)));
        if (words.length) score = Math.round((hits.length / words.length) * 60);
      }
      if (score > bestScore) { bestScore = score; best = p; }
    }
    return best && bestScore >= 40 ? { product: best, confidence: bestScore } : null;
  };

  const handleFiles = (fileList) => {
    const items = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    const previews = items.map(f => ({ file: f, url: URL.createObjectURL(f), name: f.name, path: f.webkitRelativePath || f.name }));
    const matched = previews.map(item => {
      // Try every path segment (folders + filename) and use the best-scoring match.
      // Parent folder names like "Knitted Button Down Top (Brown Stripe)" contain the product info.
      const segments = item.path.split('/').map(s => s.replace(/\.[^.]+$/, ''));
      let best = null, bestScore = 0;
      for (const seg of segments) {
        const result = fuzzyMatch(seg, products);
        if (result && result.confidence > bestScore) { best = result; bestScore = result.confidence; }
      }
      return { ...item, matchedId: best?.product?.id || null, matchedTitle: best?.product?.title || '', confidence: best?.confidence || 0 };
    });
    setFiles(previews);
    setMatches(matched);
  };

  const setMatch = (idx, productId) => {
    setMatches(prev => prev.map((m, i) => {
      if (i !== idx) return m;
      const p = products.find(p => p.id === productId);
      return { ...m, matchedId: productId, matchedTitle: p?.title || '' };
    }));
  };

  const handleUpload = async () => {
    const toUpload = matches.filter(m => m.matchedId);
    if (!toUpload.length) return;
    setUploading(true);
    const result = {}; // productId → url
    try {
      for (let i = 0; i < toUpload.length; i++) {
        const m = toUpload[i];
        setProgress(`Uploading ${i + 1} / ${toUpload.length}: ${m.name}`);
        const path = `cads/${m.matchedId}/${Date.now()}-${m.file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error } = await supabase.storage.from('product-images').upload(path, m.file, { upsert: true });
        if (error) { alert(`Upload failed for ${m.name}: ${error.message}`); continue; }
        const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(path);
        result[m.matchedId] = publicUrl;
      }
      onAssign(result);
      onClose();
    } catch (e) {
      alert('Upload error: ' + e.message);
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  const unmatched = matches.filter(m => !m.matchedId);
  const matched   = matches.filter(m => m.matchedId);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:C.white, borderRadius:14, padding:28, width:680, maxWidth:'94vw', maxHeight:'90vh', overflowY:'auto', display:'flex', flexDirection:'column', gap:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700 }}>Upload CAD Images</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:C.muted }}>✕</button>
        </div>

        {files.length === 0 ? (
          <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, border:`2px dashed ${C.e8}`, borderRadius:10, padding:40, cursor:'pointer', color:C.muted, fontSize:13, textAlign:'center' }}>
            <span style={{ fontSize:32 }}>📁</span>
            <span>Drop CAD images here or click to select a folder</span>
            <span style={{ fontSize:11 }}>Folder names and filenames are matched against SKU or product name</span>
            <input type="file" accept="image/*" multiple webkitdirectory="" style={{ display:'none' }} onChange={e => handleFiles(e.target.files)} />
          </label>
        ) : (
          <>
            {matched.length > 0 && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:C.muted, marginBottom:8 }}>MATCHED ({matched.length} image{matched.length !== 1 ? 's' : ''})</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {matches.map((m, i) => m.matchedId ? (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:C.fa, border:`1px solid ${C.e8}`, borderRadius:8 }}>
                      <img src={m.url} alt={m.name} style={{ width:52, height:52, objectFit:'contain', borderRadius:4, border:`1px solid ${C.e8}`, background:'#fff' }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.name}</div>
                        <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>→ {m.matchedTitle}</div>
                      </div>
                      <span style={{ fontSize:10, color:'#059669', background:'#d1fae5', borderRadius:6, padding:'2px 8px', flexShrink:0 }}>{m.confidence}% match</span>
                      <select value={m.matchedId} onChange={e => setMatch(i, e.target.value)}
                        style={{ fontSize:11, border:`1px solid ${C.e8}`, borderRadius:6, padding:'4px 6px', background:C.white, color:C.text, maxWidth:160 }}>
                        {products.map(p => <option key={p.id} value={p.id}>{p.title || p.skuBase || p.id}</option>)}
                      </select>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}
            {unmatched.length > 0 && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'#b45309', marginBottom:8 }}>UNMATCHED: assign manually</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {matches.map((m, i) => !m.matchedId ? (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', background:'#fffbeb', border:`1px solid #fcd34d`, borderRadius:8 }}>
                      <img src={m.url} alt={m.name} style={{ width:52, height:52, objectFit:'contain', borderRadius:4, border:`1px solid ${C.e8}`, background:'#fff' }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.name}</div>
                        <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>No match found</div>
                      </div>
                      <select value={m.matchedId || ''} onChange={e => setMatch(i, e.target.value)}
                        style={{ fontSize:11, border:`1px solid #fcd34d`, borderRadius:6, padding:'4px 6px', background:C.white, color:C.text, maxWidth:200 }}>
                        <option value=''>Assign to product</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.title || p.skuBase || p.id}</option>)}
                      </select>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', paddingTop:8, borderTop:`1px solid ${C.e8}` }}>
              <label style={{ ...btn('secondary'), cursor:'pointer', display:'inline-block' }}>
                Replace files
                <input type="file" accept="image/*" multiple webkitdirectory="" style={{ display:'none' }} onChange={e => handleFiles(e.target.files)} />
              </label>
              {uploading ? (
                <span style={{ fontSize:12, color:C.muted, alignSelf:'center' }}>{progress}</span>
              ) : (
                <button style={btn('primary')} onClick={handleUpload} disabled={matched.length === 0}>
                  Upload {matched.length} CAD{matched.length !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SPRINGBOARD VIEW
// ─────────────────────────────────────────────────────────────

export function SpringboardView({ collection, roles, onUpdateProducts, launchCalendar = [], priceTable = DEFAULT_PRICE_TABLE }) {
  const tenantConfig = useTenantConfig();
  const { territories, translationLangs, groupingField, groupingLabel } = tenantConfig;
  const tableCols = getTableCols(territories);
  const canEdit      = roles?.includes('manager') || roles?.includes('creative') || roles?.includes('technical');
  const canPrice     = roles?.includes('manager') || roles?.includes('trading');
  const canCreative  = roles?.includes('manager') || roles?.includes('creative');
  const canTechnical = roles?.includes('manager') || roles?.includes('technical');
  const canGroupEdit = canCreative || canTechnical;
  const products   = collection?.products || [];
  const [showCadUpload, setShowCadUpload]   = useState(false);
  const [draftPrices, setDraftPrices]       = useState({});
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [usdToGbp, setUsdToGbp]            = useState(null);
  const [groupByColour, setGroupByColour]   = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupDrafts, setGroupDrafts]       = useState({});
  const [groupTransLang, setGroupTransLang] = useState(translationLangs[0]?.code ?? null);

  useEffect(() => {
    fetch('https://api.frankfurter.app/latest?from=USD&to=GBP')
      .then(r => r.json())
      .then(d => { if (d?.rates?.GBP) setUsdToGbp(d.rates.GBP); })
      .catch(() => {});
  }, []);

  const toggleGroupEdit = (style, groupProducts) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(style)) {
        next.delete(style);
      } else {
        next.add(style);
        setGroupDrafts(d => {
          if (d[style]) return d;
          const fp = field => groupProducts.find(p => p[field]);
          const initTrans = Object.fromEntries(translationLangs.map(l => [l.code, {
            title:            groupProducts.find(p => p.translations?.[l.code]?.title)?.translations?.[l.code]?.title || '',
            bodyHtml:         groupProducts.find(p => p.translations?.[l.code]?.bodyHtml)?.translations?.[l.code]?.bodyHtml || '',
            composition:      groupProducts.find(p => p.translations?.[l.code]?.composition)?.translations?.[l.code]?.composition || '',
            fitFabric:        groupProducts.find(p => p.translations?.[l.code]?.fitFabric)?.translations?.[l.code]?.fitFabric || '',
            careInstructions: groupProducts.find(p => p.translations?.[l.code]?.careInstructions)?.translations?.[l.code]?.careInstructions || '',
          }]));
          return { ...d, [style]: {
            bodyHtml:         fp('bodyHtml')?.bodyHtml || '',
            fitFabric:        fp('fitFabric')?.fitFabric || '',
            careInstructions: fp('careInstructions')?.careInstructions || '',
            translations:     initTrans,
          }};
        });
      }
      return next;
    });
  };

  const applyGroupDraft = (style, groupProducts) => {
    const draft = groupDrafts[style] || {};
    const gpIds = new Set(groupProducts.map(p => p.id));
    onUpdateProducts(products.map(p => {
      if (!gpIds.has(p.id)) return p;
      const mergedTranslations = draft.translations
        ? Object.fromEntries(translationLangs.map(l => {
            const existing = p.translations?.[l.code] || {};
            const draftLang = draft.translations[l.code] || {};
            return [l.code, {
              ...existing,
              ...(canCreative  ? { title: draftLang.title ?? existing.title, bodyHtml: draftLang.bodyHtml ?? existing.bodyHtml } : {}),
              ...(canTechnical ? { composition: draftLang.composition ?? existing.composition, fitFabric: draftLang.fitFabric ?? existing.fitFabric, careInstructions: draftLang.careInstructions ?? existing.careInstructions } : {}),
            }];
          }))
        : p.translations;
      return {
        ...p,
        ...(canCreative  ? { bodyHtml: draft.bodyHtml ?? p.bodyHtml } : {}),
        ...(canTechnical ? { fitFabric: draft.fitFabric ?? p.fitFabric, careInstructions: draft.careInstructions ?? p.careInstructions } : {}),
        translations: mergedTranslations,
      };
    }));
  };

  const handleAssignCads = (cadMap) => {
    onUpdateProducts(products.map(p => cadMap[p.id] ? { ...p, cadImage: cadMap[p.id] } : p));
  };

  const hasPriceChanges = products.some(p => {
    const draft = draftPrices[p.id];
    return draft !== undefined && draft !== String(p.basePrice ?? '');
  });

  const syncPrices = () => {
    const updated = products.map(p => {
      const draft = draftPrices[p.id];
      if (draft === undefined || draft === String(p.basePrice ?? '')) return p;
      const territoryPrices = Object.fromEntries(
        territories.map(t => [t.code, draft ? convertPrice(draft, t.col, priceTable, tableCols) : ''])
      );
      return { ...p, basePrice: draft, territoryPrices };
    });
    onUpdateProducts(updated);
    setDraftPrices({});
  };

  const launchDate = (() => {
    if (!collection?.name || !launchCalendar.length) return null;
    const norm = s => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ');
    const collWords = norm(collection.name).split(/\s+/).filter(Boolean);
    const collSet = new Set(collWords);
    const matches = launchCalendar
      .filter(e => e.date && e.name)
      .filter(e => {
        const evWords = norm(e.name).split(/\s+/).filter(Boolean);
        const shared = evWords.filter(w => collSet.has(w)).length;
        return collWords.length > 0 && shared / collWords.length >= 0.75;
      })
      .sort((a, b) => a.date.localeCompare(b.date));
    return matches[0]?.date || null;
  })();

  return (
    <div style={{ padding:'24px 28px', maxWidth:1200, margin:'0 auto' }}>
      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'baseline', gap:16, marginBottom:28 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, fontFamily:F.display }}>{collection?.name}</h2>
        {(launchDate || collection?.month) && (
          <span style={{ fontSize:13, color:C.muted }}>
            Launch: {launchDate ? new Date(launchDate + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : collection.month}
          </span>
        )}
      </div>

      {/* ── Product grid ── */}
      <div style={{ borderTop:`1px solid ${C.e8}`, paddingTop:24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
          <div>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Collection Overview</h3>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:2 }}>
              <span style={{ fontSize:12, color:C.muted }}>{products.length} product{products.length !== 1 ? 's' : ''}</span>
              {usdToGbp && <span style={{ fontSize:11, color:C.ph }}>Margin calculated at $1 = £{usdToGbp.toFixed(4)}</span>}
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {canPrice && hasPriceChanges && (
              <button style={btn('primary')} onClick={syncPrices}>Sync prices to Trading</button>
            )}
            {products.length > 0 && groupingField && (
              <button style={{ ...btn('secondary'), background: groupByColour ? C.text : C.white, color: groupByColour ? C.white : C.text }} onClick={() => setGroupByColour(g => !g)}>
                Group by style
              </button>
            )}
            {canEdit && products.length > 0 && (
              <button style={btn('secondary')} onClick={() => setShowCadUpload(true)}>Upload CADs</button>
            )}
          </div>
        </div>

        {products.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:C.muted, fontSize:13, background:C.fa, border:`1px solid ${C.e8}`, borderRadius:10 }}>
            No products in this collection yet.
          </div>
        ) : (() => {
          // Build ordered list of items: style headers (when grouped) + cards.
          // Singletons/unmatched products (key=null) render as plain cards rather than being
          // lumped together, so unrelated single-item products never end up sharing one header.
          const items = [];
          if (groupByColour) {
            buildProductGroups(products, tenantConfig).forEach(({ key, items: groupProducts }) => {
              if (key && groupProducts.length > 1) {
                items.push({ type: 'header', style: key, groupProducts });
                if (canGroupEdit) items.push({ type: 'groupEdit', style: key, groupProducts });
                groupProducts.forEach(p => items.push({ type: 'card', p }));
              } else {
                groupProducts.forEach(p => items.push({ type: 'card', p }));
              }
            });
          } else {
            products.forEach(p => items.push({ type: 'card', p }));
          }
          return (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:16 }}>
              {items.map((item) => {
                if (item.type === 'header') {
                  const { style, groupProducts } = item;
                  const gd = groupProducts.find(p => draftPrices[p.id] !== undefined);
                  const gb = groupProducts.find(p => p.basePrice);
                  const groupPrice = gd ? draftPrices[gd.id] : (gb ? String(gb.basePrice) : '');
                  const groupDirty = groupProducts.some(p => { const d = draftPrices[p.id]; return d !== undefined && d !== String(p.basePrice ?? ''); });
                  return (
                    <div key={`hdr-${style}`} style={{ gridColumn:'1 / -1', display:'flex', alignItems:'center', gap:12, paddingTop:8, paddingBottom:6, borderBottom:`1px solid ${C.e8}`, marginTop:8 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{style}</span>
                      <span style={{ fontSize:11, color:C.muted }}>{groupProducts.length} {groupingLabel || 'colour'}{groupProducts.length !== 1 ? 's' : ''}</span>
                      {canGroupEdit && (
                        <button onClick={() => toggleGroupEdit(style, groupProducts)}
                          style={{ fontSize:11, color: expandedGroups.has(style) ? C.text : C.muted, background:'none', border:'none', cursor:'pointer', padding:'2px 4px' }}>
                          {expandedGroups.has(style) ? 'Hide ▴' : 'Edit copy & tech ▾'}
                        </button>
                      )}
                      {canPrice && (
                        <div style={{ display:'flex', alignItems:'center', gap:4, marginLeft:'auto' }}>
                          <span style={{ fontSize:12, color:C.muted }}>Price all: £</span>
                          <input type="number" value={groupPrice} placeholder="-"
                            onChange={e => { const v = e.target.value; setDraftPrices(prev => ({ ...prev, ...Object.fromEntries(groupProducts.map(p => [p.id, v])) })); }}
                            style={{ width:70, fontSize:13, fontWeight:600, border:`1px solid ${groupDirty ? '#a78bfa' : C.e8}`, borderRadius:6, padding:'3px 6px', color: groupDirty ? '#7c3aed' : C.text }} />
                        </div>
                      )}
                    </div>
                  );
                }
                if (item.type === 'groupEdit') {
                  const { style, groupProducts } = item;
                  if (!expandedGroups.has(style)) return null;
                  const draft = groupDrafts[style] || {};
                  return (
                    <div key={`gedit-${style}`} style={{ gridColumn:'1 / -1', background:C.fa, border:`1px solid ${C.e8}`, borderRadius:10, padding:16, marginBottom:4, display:'flex', flexDirection:'column', gap:12 }}>
                      {canCreative && (
                        <div>
                          <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>DESCRIPTION</label>
                          <textarea
                            value={draft.bodyHtml || ''}
                            onChange={e => setGroupDrafts(d => ({ ...d, [style]: { ...d[style], bodyHtml: e.target.value } }))}
                            rows={4}
                            placeholder="Product description (HTML or plain text)…"
                            style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'8px 10px', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box', background:C.white }}
                          />
                        </div>
                      )}
                      {canTechnical && (
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                          <div>
                            <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>FIT &amp; FABRIC</label>
                            <textarea
                              value={draft.fitFabric || ''}
                              onChange={e => setGroupDrafts(d => ({ ...d, [style]: { ...d[style], fitFabric: e.target.value } }))}
                              rows={3}
                              placeholder="Fit &amp; fabric details…"
                              style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'8px 10px', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box', background:C.white }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>CARE INSTRUCTIONS</label>
                            <textarea
                              value={draft.careInstructions || ''}
                              onChange={e => setGroupDrafts(d => ({ ...d, [style]: { ...d[style], careInstructions: e.target.value } }))}
                              rows={3}
                              placeholder="Care instructions…"
                              style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'8px 10px', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box', background:C.white }}
                            />
                          </div>
                        </div>
                      )}
                      {/* ── Translations ── */}
                      {(canCreative || canTechnical) && translationLangs.length > 0 && (
                        <div style={{ borderTop:`1px solid ${C.e8}`, paddingTop:12 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:C.muted, marginBottom:10 }}>TRANSLATIONS</div>
                          <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:12 }}>
                            {translationLangs.map(l => {
                              const t = draft.translations?.[l.code] || {};
                              const filled = canCreative
                                ? (t.title || t.bodyHtml)
                                : (t.composition || t.fitFabric || t.careInstructions);
                              const active = groupTransLang === l.code;
                              return (
                                <button key={l.code} onClick={() => setGroupTransLang(l.code)}
                                  style={{ fontSize:11, padding:'3px 10px', borderRadius:20, border:`1px solid ${active ? C.text : C.e8}`, background: active ? C.text : C.white, color: active ? C.white : filled ? C.text : C.muted, cursor:'pointer', fontWeight: filled ? 600 : 400 }}>
                                  {l.flag} {l.code}
                                </button>
                              );
                            })}
                          </div>
                          {(() => {
                            const lang = translationLangs.find(l => l.code === groupTransLang);
                            if (!lang) return null;
                            const t = draft.translations?.[groupTransLang] || {};
                            const updT = (field, val) => setGroupDrafts(d => ({
                              ...d,
                              [style]: { ...d[style], translations: { ...(d[style]?.translations || {}), [groupTransLang]: { ...(d[style]?.translations?.[groupTransLang] || {}), [field]: val } } }
                            }));
                            return (
                              <div style={{ background:C.white, border:`1px solid ${C.e8}`, borderRadius:8, padding:14, display:'flex', flexDirection:'column', gap:10 }}>
                                <div style={{ fontSize:11, color:C.muted }}>{lang.flag} {lang.label}</div>
                                {canCreative && (
                                  <>
                                    <div>
                                      <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>TITLE ({lang.code})</label>
                                      <input value={t.title || ''} onChange={e => updT('title', e.target.value)}
                                        placeholder={`Translated title…`}
                                        style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'6px 10px', boxSizing:'border-box' }} />
                                    </div>
                                    <div>
                                      <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>DESCRIPTION ({lang.code})</label>
                                      <textarea value={t.bodyHtml || ''} onChange={e => updT('bodyHtml', e.target.value)}
                                        rows={3} placeholder="Translated description…"
                                        style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'8px 10px', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
                                    </div>
                                  </>
                                )}
                                {canTechnical && (
                                  <>
                                    <div>
                                      <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>COMPOSITION ({lang.code})</label>
                                      <input value={t.composition || ''} onChange={e => updT('composition', e.target.value)}
                                        placeholder="Translated composition…"
                                        style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'6px 10px', boxSizing:'border-box' }} />
                                    </div>
                                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                                      <div>
                                        <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>FIT &amp; FABRIC ({lang.code})</label>
                                        <textarea value={t.fitFabric || ''} onChange={e => updT('fitFabric', e.target.value)}
                                          rows={3} placeholder="Translated fit & fabric…"
                                          style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'8px 10px', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
                                      </div>
                                      <div>
                                        <label style={{ fontSize:11, fontWeight:600, color:C.muted, display:'block', marginBottom:4 }}>CARE INSTRUCTIONS ({lang.code})</label>
                                        <textarea value={t.careInstructions || ''} onChange={e => updT('careInstructions', e.target.value)}
                                          rows={3} placeholder="Translated care instructions…"
                                          style={{ width:'100%', fontSize:12, border:`1px solid ${C.e8}`, borderRadius:6, padding:'8px 10px', fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      <div style={{ display:'flex', justifyContent:'flex-end' }}>
                        <button style={btn('primary')} onClick={() => applyGroupDraft(style, groupProducts)}>
                          Apply to all {groupProducts.length} colour{groupProducts.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    </div>
                  );
                }

                const { p } = item;
                const draftPrice = draftPrices[p.id];
                const displayPrice = draftPrice !== undefined ? draftPrice : (p.basePrice ?? '');
                const isDirty = draftPrice !== undefined && draftPrice !== String(p.basePrice ?? '');
                const costs = (p.variants || []).map(v => parseFloat(v.costPerItem)).filter(n => !isNaN(n) && n > 0);
                const avgCost = costs.length ? costs.reduce((a, b) => a + b, 0) / costs.length : null;
                const sellPrice = parseFloat(displayPrice);
                const margin = avgCost && sellPrice > 0 ? ((sellPrice - avgCost) / sellPrice) * 100 : null;
                const marginColor = margin >= 60 ? '#059669' : margin >= 40 ? '#d97706' : '#dc2626';
                return (
                  <div key={p.id} style={{ background:C.white, border:`1px solid ${isDirty ? '#a78bfa' : C.e8}`, borderRadius:10, overflow:'hidden', display:'flex', flexDirection:'column', transition:'border-color 0.15s' }}>
                    <div style={{ aspectRatio:'3/4', background:C.fa, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                      {p.cadImage ? <img src={p.cadImage} alt={p.title} style={{ width:'100%', height:'100%', objectFit:'contain' }} /> : <div style={{ fontSize:11, color:C.ph, textAlign:'center', padding:12 }}>No CAD</div>}
                    </div>
                    <div style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:3, flex:1 }}>
                      <div style={{ fontSize:11, fontFamily:F.mono, color:C.muted }}>{p.skuBase}</div>
                      <div style={{ fontSize:12, fontWeight:600, lineHeight:1.3 }}>{p.title || '-'}</div>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'auto', paddingTop:6, gap:6 }}>
                        {canPrice ? (editingPriceId === p.id ? (
                          <div style={{ display:'flex', alignItems:'center', gap:3 }}>
                            <span style={{ fontSize:12, color:C.muted }}>£</span>
                            <input autoFocus type="number" value={displayPrice}
                              onChange={e => setDraftPrices(prev => ({ ...prev, [p.id]: e.target.value }))}
                              onBlur={() => setEditingPriceId(null)}
                              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingPriceId(null); }}
                              style={{ width:64, fontSize:13, fontWeight:600, border:`1px solid ${C.e8}`, borderRadius:4, padding:'2px 4px', fontFamily:F.body }} />
                          </div>
                        ) : (
                          <span onClick={() => setEditingPriceId(p.id)} style={{ fontSize:13, fontWeight:600, color: isDirty ? '#7c3aed' : C.text, borderBottom:`1px dashed ${isDirty ? '#c4b5fd' : C.e8}`, paddingBottom:2, cursor:'text' }}>
                            {displayPrice ? `£${displayPrice}` : <span style={{ color:C.ph, fontWeight:400 }}>Set price…</span>}
                          </span>
                        )) : (displayPrice ? <span style={{ fontSize:12, fontWeight:600, color:C.text }}>£{displayPrice}</span> : <span />)}
                        {margin !== null && <span style={{ fontSize:10, fontWeight:600, color: marginColor }}>{margin.toFixed(0)}%</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ── Price conversion table ── */}
      {products.some(p => p.basePrice) && (() => {
        const displayTerr = territories.filter(t => t.code !== 'STG' && t.code !== 'DE' && t.code !== 'FR');
        const uniquePrices = [...new Set(
          products.map(p => {
            const draft = draftPrices[p.id];
            return draft !== undefined ? draft : String(p.basePrice ?? '');
          }).filter(Boolean)
        )].map(Number).filter(n => n > 0).sort((a, b) => a - b);

        if (!uniquePrices.length) return null;
        return (
          <div style={{ marginTop:32, borderTop:`1px solid ${C.e8}`, paddingTop:24 }}>
            <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Price Conversions</h3>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:'left', padding:'6px 12px', borderBottom:`2px solid ${C.e8}`, fontWeight:600, color:C.muted, whiteSpace:'nowrap' }}>GBP</th>
                    {displayTerr.filter(t => t.code !== 'UK').map(t => (
                      <th key={t.code} style={{ textAlign:'right', padding:'6px 12px', borderBottom:`2px solid ${C.e8}`, fontWeight:600, color:C.muted, whiteSpace:'nowrap' }}>
                        {t.flag} {t.currency}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uniquePrices.map((gbp, i) => (
                    <tr key={gbp} style={{ background: i % 2 === 0 ? C.white : C.fa }}>
                      <td style={{ padding:'8px 12px', fontWeight:700, borderBottom:`1px solid ${C.e8}` }}>£{gbp}</td>
                      {displayTerr.filter(t => t.code !== 'UK').map(t => {
                        const converted = convertPrice(gbp, t.col, priceTable, tableCols);
                        return (
                          <td key={t.code} style={{ textAlign:'right', padding:'8px 12px', color:C.muted, borderBottom:`1px solid ${C.e8}`, fontFamily:F.mono, fontSize:11 }}>
                            {converted ? `${t.symbol}${converted}` : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {showCadUpload && (
        <CadUploadModal
          products={products}
          onAssign={handleAssignCads}
          onClose={() => setShowCadUpload(false)}
        />
      )}
    </div>
  );
}
